import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BookOpen, ChevronLeft, CircleCheck, Clock, ListFilter, Mail, MessageSquare, NotebookPen, Plus, Route as RouteIcon, Shield, Star, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import Dialog from "@/components/modals/Dialog";
import { type ThreadItem } from "@/components/student/ThreadList";
import WorkThread, { messageSubmissionId, type WorkThreadData } from "@/components/courses/WorkThread";
import { api, errorMessage, post } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import { ITEM_LABEL } from "@/pages/student/itemTypes";
import { gradeText } from "@/lib/gradeLabel";
import { MyUpcomingEvents } from "@/components/events/MyEvents";
import "./Schools.css";
import "@/styles/student.css";

type ThreadsData = {
  students: Array<{ studentUserId: string; studentName: string }>;
  items: ThreadItem[];
};

type ThreadData = WorkThreadData & {
  student: { studentUserId: string; studentName: string };
  activity: { id: string; title: string; type: string };
  course: { id: string; title: string };
  replyBlock: "no_permission" | "not_started" | "chat_off" | null;
};

type CourseGrade = {
  percent: number | null;
  letter: string | null;
  awaitingCount?: number;
};

type CourseRow = {
  id: string;
  title: string;
  activitiesDone?: number;
  activitiesTotal?: number;
  hasPublishedWork?: boolean;
  grade?: CourseGrade;
};

type CourseListData = {
  student: { studentUserId: string; studentName: string };
  canOpenContent: boolean;
  otherSchoolCount: number;
  courses: CourseRow[];
};

type ActivityScore = {
  id: string;
  type: string;
  title: string;
  unitTitle: string | null;
  lessonTitle: string | null;
  percent: number | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  awaiting: boolean;
  completed: boolean;
};

type CourseContent = {
  student: { studentUserId: string; studentName: string };
  course: { id: string; title: string };
  activities: ActivityScore[];
};

type RoomSummary = {
  audience: "public" | "staff" | "personal" | string;
  body: string;
  authorRole: string | null;
  authorName: string | null;
  at: string;
  mine?: boolean;
};

type ConversationData = {
  student: { studentUserId: string; studentName: string; studentEmail?: string | null };
  canPost: boolean;
  rooms: RoomSummary[];
};

type TimelineFilter = "all" | "room" | "work" | "mine" | "student" | "staff";

type TimelineRow = {
  key: string;
  kind: "room" | "activity";
  channel?: "public" | "staff" | "personal";
  badge: string;
  title: string;
  detail: string;
  snippet: string;
  at: string | null;
  href: string;
  mine: boolean;
  studentPosted: boolean;
  staffPosted: boolean;
};

type RoomData = WorkThreadData & {
  student: { studentUserId: string; studentName: string };
};

const ROOM_META = {
  public: {
    label: "Student",
    kicker: "+ Staff",
    hint: "Shared with the student and with staff who can open this room.",
    empty: "No messages yet.",
    placeholder: "Write a message",
  },
  staff: {
    label: "Staff",
    hint: "Staff only. The student does not see this.",
    empty: "No staff messages yet.",
    placeholder: "Write a staff message",
  },
  personal: {
    label: "Notes",
    hint: "Only you can see these notes.",
    empty: "No notes yet.",
    placeholder: "Write a note",
  },
};

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function courseMeta(course: CourseRow) {
  if (!course.hasPublishedWork) return "Nothing published yet";
  const progress = `${course.activitiesDone || 0} of ${course.activitiesTotal || 0} complete`;
  const grade = gradeText(course.grade);
  const waiting = course.grade?.awaitingCount ? `${course.grade.awaitingCount} waiting` : "";
  return [progress, grade, waiting].filter(Boolean).join(" · ");
}

function scoreLabel(item: ActivityScore) {
  if (item.percent != null) return `${item.percent}%`;
  if (item.awaiting) return "Waiting";
  if (item.completed) return "Done";
  return "Not done";
}

function outlineGroups(activities: ActivityScore[]) {
  const groups: Array<{ key: string; unitTitle: string | null; lessonTitle: string | null; items: ActivityScore[] }> = [];
  for (const item of activities) {
    const key = `${item.unitTitle || ""}\n${item.lessonTitle || ""}`;
    const last = groups[groups.length - 1];
    if (!last || last.key !== key) {
      groups.push({ key, unitTitle: item.unitTitle, lessonTitle: item.lessonTitle, items: [item] });
    } else {
      last.items.push(item);
    }
  }
  return groups;
}

type FeedStudent = { studentUserId: string; studentName: string };

type FeedEvent = {
  id: string;
  kind: "conversation" | "course";
  courseEvent: "enrolled" | "completed" | "graded" | null;
  channel: "public" | "staff" | "personal" | "work" | null;
  studentUserId: string;
  studentName: string;
  title: string;
  detail: string;
  snippet: string;
  at: string | null;
  courseId: string | null;
  activityId: string | null;
};

type DueEvent = {
  id: string;
  studentUserId: string;
  studentName: string;
  title: string;
  detail: string;
  at: string | null;
  overdue: boolean;
  courseId: string | null;
  activityId: string | null;
};

type FeedData = {
  students: FeedStudent[];
  canChat: boolean;
  canCourses: boolean;
  canOpenContent: boolean;
  events: FeedEvent[];
  comingDue?: DueEvent[];
};

type FeedKind = "all" | "conversations" | "course";
type FeedView = "time" | "student";
type FeedList = "recent" | "due";

function dueDateLabel(iso: string) {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return "";
  return due.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(due.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" as const }),
  });
}

function courseEventLabel(event: FeedEvent["courseEvent"]) {
  if (event === "enrolled") return "Enrolled";
  if (event === "completed") return "Completed";
  if (event === "graded") return "Graded";
  return "Course activity";
}

function conversationLabel(channel: FeedEvent["channel"]) {
  if (channel === "staff") return "Staff";
  if (channel === "personal") return "Notes";
  if (channel === "work") return "Coursework";
  return "Student and staff";
}

function StudentHome() {
  const navigate = useNavigate();
  const { pageAccess, loading: authLoading } = useAuth();
  const canOpen = Boolean(pageAccess?.["my-students"]);
  const [feed, setFeed] = useState<FeedData | null>(null);
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [kind, setKind] = useState<FeedKind>("all");
  const [view, setView] = useState<FeedView>("time");
  const [list, setList] = useState<FeedList>("recent");
  const [pickOpen, setPickOpen] = useState(false);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    let cancel = false;
    api<{ success: boolean; data: FeedData }>("/api/my-students/feed")
      .then((env) => {
        if (!cancel) setFeed(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [authLoading, canOpen]);

  if (authLoading || (canOpen && !error && !feed)) return <LoadingOverlay isVisible message="Loading…" />;
  if (!canOpen) return <Navigate to="/" replace state={accessDeniedNavigateState("my-students")} />;

  const students = feed?.students || [];
  const onlyOne = students.length < 2;
  const showingDue = list === "due" && Boolean(feed?.canOpenContent);
  const events = (feed?.events || []).filter((event) => {
    if (studentId && event.studentUserId !== studentId) return false;
    if (kind === "conversations") return event.kind === "conversation";
    if (kind === "course") return event.kind === "course";
    return true;
  });
  const dueEvents = (feed?.comingDue || []).filter((event) => !studentId || event.studentUserId === studentId);
  const groups = view === "student"
    ? students
        .filter((student) => !studentId || student.studentUserId === studentId)
        .map((student) => ({
          student,
          events: events.filter((event) => event.studentUserId === student.studentUserId),
        }))
        .filter((group) => group.events.length > 0)
    : [{ student: null as FeedStudent | null, events }];
  const dueGroups = view === "student"
    ? students
        .filter((student) => !studentId || student.studentUserId === studentId)
        .map((student) => ({
          student,
          events: dueEvents.filter((event) => event.studentUserId === student.studentUserId),
        }))
        .filter((group) => group.events.length > 0)
    : [{ student: null as FeedStudent | null, events: dueEvents }];

  const renderEvent = (event: FeedEvent) => {
    const conversation = event.kind === "conversation";
    const href = conversation
      ? event.channel === "work"
        ? `/my-students/${encodeURIComponent(event.studentUserId)}/activity/${encodeURIComponent(event.activityId || "")}`
        : event.channel === "public" || !event.channel
          ? `/my-students/${encodeURIComponent(event.studentUserId)}/room`
          : `/my-students/${encodeURIComponent(event.studentUserId)}/room?channel=${event.channel}`
      : "";
    const badge = conversation ? conversationLabel(event.channel) : courseEventLabel(event.courseEvent);
    const pathHref = !conversation && feed?.canOpenContent && event.courseId
      ? `/my-students/${encodeURIComponent(event.studentUserId)}/course/${encodeURIComponent(event.courseId)}`
      : "";
    const submissionHref = !conversation && event.activityId && feed?.canChat
      ? `/my-students/${encodeURIComponent(event.studentUserId)}/activity/${encodeURIComponent(event.activityId)}`
      : "";
    const cardHref = conversation ? href : submissionHref || pathHref;
    const body = (
      <>
        <span className={`al-my-students-badge al-my-students-badge--${conversation ? "talk" : event.courseEvent || "course"}`}>
          {conversation ? <MessageSquare size={13} aria-hidden /> : event.courseEvent === "completed" ? <CircleCheck size={13} aria-hidden /> : event.courseEvent === "graded" ? <Star size={13} aria-hidden /> : <BookOpen size={13} aria-hidden />}
          {badge}
        </span>
        <span className="al-my-students-timeline__main">
          <strong>{event.title || badge}</strong>
          <span>
            {event.studentName}
            {event.detail ? ` · ${event.detail}` : ""}
          </span>
          {event.snippet ? <span className="al-my-students-timeline__snippet">{event.snippet}</span> : null}
          {!conversation && !cardHref ? (
            <span className="al-my-students-timeline__actions">
              <span>You do not have permission to open this.</span>
            </span>
          ) : null}
        </span>
        <time dateTime={event.at || undefined}>{event.at ? whenLabel(event.at) : ""}</time>
        {pathHref ? (
          <Link
            className="al-my-students-timeline__path"
            to={pathHref}
            aria-label="View path"
            title="View path"
            onClick={(click) => click.stopPropagation()}
            onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
          >
            <RouteIcon size={16} aria-hidden />
          </Link>
        ) : (
          <span className="al-my-students-timeline__path" aria-hidden />
        )}
      </>
    );
    if (conversation) {
      return (
        <Link key={event.id} className="al-my-students-timeline__row" to={href}>
          {body}
        </Link>
      );
    }
    return (
      <article
        key={event.id}
        className={`al-my-students-timeline__row${cardHref ? " is-clickable" : ""}`}
        role={cardHref ? "link" : undefined}
        tabIndex={cardHref ? 0 : undefined}
        onClick={() => {
          if (cardHref) navigate(cardHref);
        }}
        onKeyDown={(keyEvent) => {
          if (!cardHref || (keyEvent.key !== "Enter" && keyEvent.key !== " ")) return;
          keyEvent.preventDefault();
          navigate(cardHref);
        }}
      >
        {body}
      </article>
    );
  };

  const renderDue = (event: DueEvent) => {
    const pathHref = event.courseId
      ? `/my-students/${encodeURIComponent(event.studentUserId)}/course/${encodeURIComponent(event.courseId)}`
      : "";
    const workHref = event.activityId && feed?.canChat
      ? `/my-students/${encodeURIComponent(event.studentUserId)}/activity/${encodeURIComponent(event.activityId)}`
      : "";
    const cardHref = workHref || pathHref;
    return (
      <article
        key={event.id}
        className={`al-my-students-timeline__row${cardHref ? " is-clickable" : ""}`}
        role={cardHref ? "link" : undefined}
        tabIndex={cardHref ? 0 : undefined}
        onClick={() => {
          if (cardHref) navigate(cardHref);
        }}
        onKeyDown={(keyEvent) => {
          if (!cardHref || (keyEvent.key !== "Enter" && keyEvent.key !== " ")) return;
          keyEvent.preventDefault();
          navigate(cardHref);
        }}
      >
        <span className={`al-my-students-badge al-my-students-badge--${event.overdue ? "overdue" : "due"}`}>
          <Clock size={13} aria-hidden />
          {event.overdue ? "Overdue" : "Due"}
        </span>
        <span className="al-my-students-timeline__main">
          <strong>{event.title}</strong>
          <span>
            {event.studentName}
            {event.detail ? ` · ${event.detail}` : ""}
          </span>
        </span>
        <time dateTime={event.at || undefined}>{event.at ? dueDateLabel(event.at) : ""}</time>
        {pathHref ? (
          <Link
            className="al-my-students-timeline__path"
            to={pathHref}
            aria-label="View path"
            title="View path"
            onClick={(click) => click.stopPropagation()}
            onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
          >
            <RouteIcon size={16} aria-hidden />
          </Link>
        ) : (
          <span className="al-my-students-timeline__path" aria-hidden />
        )}
      </article>
    );
  };

  return (
    <div className="al-schools al-my-students-messages">
      <header className="al-schools-header">
        <div className="al-schools-header-text">
          <h1>My Students</h1>
          <p className="al-schools-sub">
            {showingDue
              ? "Unfinished work with a due date for everyone linked to you, soonest first."
              : "Messages and class activity for everyone linked to you, newest first."}
          </p>
        </div>
        <div className="al-my-students-tools">
          <div className="al-my-students-view" role="group" aria-label="What to show">
            <button type="button" className={!showingDue ? "is-on" : ""} onClick={() => setList("recent")}>
              Timeline
            </button>
            {feed?.canOpenContent ? (
              <button type="button" className={showingDue ? "is-on" : ""} onClick={() => setList("due")}>
                Coming due
              </button>
            ) : null}
          </div>
          <div className="al-my-students-view" role="group" aria-label="Arrange">
            <button type="button" className={view === "student" ? "is-on" : ""} disabled={onlyOne} title={onlyOne ? "Only one student" : "Group by student"} onClick={() => setView(view === "student" ? "time" : "student")}>
              By student
            </button>
          </div>
          <button
            type="button"
            className={`al-schools-action-edit${studentId || kind !== "all" ? " is-on" : ""}`}
            aria-label="Filter timeline"
            aria-expanded={filterOpen}
            onClick={() => setFilterOpen((open) => !open)}
          >
            <ListFilter size={16} aria-hidden />
          </button>
          {filterOpen ? (
            <div className="al-my-students-filter" role="menu">
              <button type="button" className={!studentId && kind === "all" ? "is-on" : ""} onClick={() => { setStudentId(""); setKind("all"); setFilterOpen(false); }}>
                All
              </button>
              <p>Students</p>
              {students.map((student) => (
                <button
                  key={student.studentUserId}
                  type="button"
                  disabled={onlyOne}
                  title={onlyOne ? "Only one student" : undefined}
                  className={studentId === student.studentUserId ? "is-on" : ""}
                  onClick={() => { setStudentId(student.studentUserId); setFilterOpen(false); }}
                >
                  {student.studentName}
                </button>
              ))}
              {!showingDue && feed?.canChat ? (
                <button type="button" className={kind === "conversations" ? "is-on" : ""} onClick={() => { setKind("conversations"); setFilterOpen(false); }}>
                  Conversations
                </button>
              ) : null}
              {!showingDue && feed?.canCourses ? (
                <button type="button" className={kind === "course" ? "is-on" : ""} onClick={() => { setKind("course"); setFilterOpen(false); }}>
                  Course activity
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>
      <MyUpcomingEvents />
      {error ? (
        <p className="al-schools-sub" role="alert">{error}</p>
      ) : students.length === 0 ? (
        <div className="al-my-students-empty">
          <h2>No students yet</h2>
          <p>People linked to you as a guardian or a counselor show up here.</p>
        </div>
      ) : showingDue && dueEvents.length === 0 ? (
        <div className="al-my-students-empty">
          <h2>Nothing coming due</h2>
          <p>Finished work, and work with no due date, stays on Timeline.</p>
        </div>
      ) : !showingDue && events.length === 0 ? (
        <div className="al-my-students-empty">
          <h2>Nothing here yet</h2>
          <p>
            {feed?.canChat ? "Conversations show up after the first message. " : "You do not have permission to see conversations. "}
            {feed?.canCourses
              ? "Course activity covers an enrollment, a completed step, and a graded submission."
              : "You do not have permission to see course activity."}
          </p>
        </div>
      ) : showingDue ? (
        dueGroups.map((group) => (
          <section key={group.student?.studentUserId || "all"} className="al-my-students-group">
            {group.student ? <h2>{group.student.studentName}</h2> : null}
            <div className="al-my-students-timeline">
              {group.events.map(renderDue)}
            </div>
          </section>
        ))
      ) : (
        groups.map((group) => (
          <section key={group.student?.studentUserId || "all"} className="al-my-students-group">
            {group.student ? <h2>{group.student.studentName}</h2> : null}
            <div className="al-my-students-timeline">
              {group.events.map(renderEvent)}
            </div>
          </section>
        ))
      )}
      {feed?.canChat && students.length > 0 ? (
        <button
          type="button"
          className="al-msg-fab"
          onClick={() => {
            if (students.length === 1) {
              navigate(`/my-students/${encodeURIComponent(students[0].studentUserId)}/messages?compose=1`);
              return;
            }
            setPickOpen(true);
          }}
        >
          <Plus size={14} strokeWidth={2.25} aria-hidden />
          New conversation
        </button>
      ) : null}
      <Dialog open={pickOpen} title="New conversation" subtitle="Choose the student." onClose={() => setPickOpen(false)} width="28rem">
        <div className="al-my-students-start">
          {students.map((student) => (
            <Link
              key={student.studentUserId}
              className="al-my-students-start__card"
              to={`/my-students/${encodeURIComponent(student.studentUserId)}/messages?compose=1`}
              onClick={() => setPickOpen(false)}
            >
              <span className="al-my-students-start__icon"><Users size={16} aria-hidden /></span>
              <span className="al-my-students-start__copy">
                <span className="al-my-students-start__title">{student.studentName}</span>
              </span>
            </Link>
          ))}
        </div>
      </Dialog>
    </div>
  );
}

function joinList(items: string[]) {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function timelineEmptyCopy({
  canPost,
  canStaff,
  canClasses,
}: {
  canPost: boolean;
  canStaff: boolean;
  canClasses: boolean;
}) {
  const places = ["messages with the student and staff", "coursework chats", "your private notes"];
  if (canStaff) places.splice(2, 0, "staff-only messages");
  const starts = ["a private note"];
  if (canPost) starts.unshift("a message with the student and staff");
  if (canStaff) starts.push("a staff-only message");
  if (canClasses) starts.push("a coursework chat");
  const limits = [
    canPost ? "" : "Message In Student Chat is what lets you write to the student and on coursework.",
    canStaff ? "" : "A staff-only room needs Staff About Student.",
    canClasses ? "" : "Opening classes to start a coursework chat needs View Students Course Enrollments.",
  ].filter(Boolean);
  return `Once someone writes, ${joinList(places)} show up here, newest first. Use New conversation to start ${joinList(starts)}.${limits.length ? ` ${limits.join(" ")}` : ""}`;
}

function roomBadge(audience: string) {
  if (audience === "staff") return "Staff";
  if (audience === "personal") return "Notes";
  return "Student and staff";
}

function StartCard({
  icon,
  title,
  text,
  href,
  unavailable,
  onOpen,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  href?: string;
  unavailable?: string;
  onOpen: () => void;
}) {
  const body = (
    <>
      <span className="al-my-students-start__icon">{icon}</span>
      <span className="al-my-students-start__copy">
        <span className="al-my-students-start__title">
          {title}
          {unavailable ? <span className="al-my-students-start__tag">{unavailable}</span> : null}
        </span>
        <span className="al-my-students-start__text">{text}</span>
      </span>
    </>
  );
  if (href && !unavailable) {
    return (
      <Link className="al-my-students-start__card" to={href} onClick={onOpen}>
        {body}
      </Link>
    );
  }
  return <div className="al-my-students-start__card is-unavailable">{body}</div>;
}

function roomWho(room: RoomSummary) {
  if (room.mine) return "You";
  if (room.authorName) return room.authorName;
  return roomBadge(room.audience);
}

function matchesFilter(row: TimelineRow, filter: TimelineFilter) {
  if (filter === "all") return true;
  if (filter === "room") return row.kind === "room" && row.channel === "public";
  if (row.kind !== "activity") return false;
  if (filter === "work") return true;
  if (filter === "mine") return row.mine;
  if (filter === "student") return row.studentPosted;
  return row.staffPosted;
}

type DirectPerson = {
  userId: string;
  name: string;
  email: string | null;
  kind: string;
  label: string;
};

type DirectCourse = {
  id: string;
  title: string;
  teachers: Array<{ userId: string; name: string; email: string | null }>;
};

type DirectTargets = {
  people: DirectPerson[];
  courses: DirectCourse[];
};

function directHref(
  studentUserId: string,
  person: { userId: string; name: string; email?: string | null },
) {
  return `/messages?${new URLSearchParams({
    with: person.userId,
    student: studentUserId,
    name: person.name,
    ...(person.email ? { email: person.email } : {}),
  }).toString()}`;
}

function StudentMessages() {
  const { studentUserId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const { capabilities, session, loading: authLoading } = useAuth();
  const canChat = Boolean(capabilities?.viewStudentChat);
  const canPost = Boolean(capabilities?.messageInStudentChat);
  const canStaff = Boolean(capabilities?.staffStudentChat);
  const canClasses = Boolean(capabilities?.viewStudentsCourseEnrollments);
  const canDm = Boolean(capabilities?.sendDm);
  const canRelationship = Boolean(capabilities?.sendStudentRelationship);
  const canTeachers = Boolean(capabilities?.sendStudentTeacher);
  const relationshipReady = Boolean(
    session?.acuityLearn?.relationshipTypes &&
      Object.values(session.acuityLearn.relationshipTypes).some(Boolean),
  );
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [threads, setThreads] = useState<ThreadsData | null>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<TimelineFilter>("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [targets, setTargets] = useState<DirectTargets | null>(null);
  const [targetsError, setTargetsError] = useState("");

  useEffect(() => {
    if (params.get("compose") !== "1") return;
    setStartOpen(true);
    const next = new URLSearchParams(params);
    next.delete("compose");
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (!startOpen || (!canRelationship && !canTeachers)) return;
    let cancel = false;
    setTargets(null);
    setTargetsError("");
    api<{ success: boolean; data: DirectTargets }>(
      `/api/my-students/${encodeURIComponent(studentUserId)}/direct-targets`,
    )
      .then((env) => {
        if (!cancel) setTargets(env.data);
      })
      .catch((err) => {
        if (!cancel) setTargetsError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [startOpen, canRelationship, canTeachers, studentUserId]);

  useEffect(() => {
    if (authLoading || !canChat) return;
    let cancel = false;
    setConversation(null);
    setThreads(null);
    setError("");
    Promise.all([
      api<{ success: boolean; data: ConversationData }>(
        `/api/my-students/${encodeURIComponent(studentUserId)}/conversation`,
      ).then((env) => env.data),
      api<{ success: boolean; data: ThreadsData }>(
        `/api/my-students/${encodeURIComponent(studentUserId)}/threads`,
      ).then((env) => env.data),
    ])
      .then(([room, feed]) => {
        if (cancel) return;
        setConversation(room);
        setThreads(feed);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [authLoading, canChat, studentUserId]);

  if (authLoading || (canChat && !error && (!conversation || !threads))) {
    return <LoadingOverlay isVisible message="Loading messages…" />;
  }

  const name = conversation?.student.studentName || "Student";
  const roomHref = `/my-students/${encodeURIComponent(studentUserId)}/room`;
  const roomRows: TimelineRow[] = (conversation?.rooms || [])
    .filter((room) => room.body)
    .map((room) => {
      const channel = room.audience === "staff" || room.audience === "personal" ? room.audience : "public";
      return {
        key: channel,
        kind: "room" as const,
        channel,
        badge: roomBadge(channel),
        title: roomWho(room),
        detail: "",
        snippet: room.body,
        at: room.at,
        href: channel === "public" ? roomHref : `${roomHref}?channel=${channel}`,
        mine: Boolean(room.mine),
        studentPosted: false,
        staffPosted: false,
      };
    });
  const rows: TimelineRow[] = [
    ...roomRows,
    ...(threads?.items || [])
      .filter((item) => item.kind !== "student" && item.last?.body)
      .map((item) => ({
        key: item.activityId,
        kind: "activity" as const,
        badge: "Coursework",
        title: item.activityTitle,
        detail: item.courseTitle,
        snippet: item.last.body,
        at: item.lastAt,
        href: `/my-students/${encodeURIComponent(studentUserId)}/activity/${encodeURIComponent(item.activityId)}`,
        mine: item.viewerPosted,
        studentPosted: item.studentPosted,
        staffPosted: item.teacherFeedback,
      })),
  ];
  rows.sort((a, b) => {
    if (!a.at && !b.at) return 0;
    if (!a.at) return 1;
    if (!b.at) return -1;
    return new Date(b.at).getTime() - new Date(a.at).getTime();
  });
  const shown = rows.filter((row) => matchesFilter(row, filter));

  return (
    <div className="al-schools al-my-students-messages">
      <header className="al-schools-header">
        <div className="al-schools-header-text">
          <Link className="al-schools-sub" to="/my-students">
            Students
          </Link>
          <h1>{name}</h1>
        </div>
        {canChat ? (
          <div className="al-my-students-tools">
            <button
              type="button"
              className={`al-schools-action-edit${filter !== "all" ? " is-on" : ""}`}
              aria-label="Filter timeline"
              aria-expanded={filterOpen}
              title="Filter"
              onClick={() => setFilterOpen((open) => !open)}
            >
              <ListFilter size={16} aria-hidden />
            </button>
            {filterOpen ? (
              <div className="al-my-students-filter" role="menu">
                <button type="button" className={filter === "all" ? "is-on" : ""} onClick={() => { setFilter("all"); setFilterOpen(false); }}>
                  All
                </button>
                <button type="button" className={filter === "room" ? "is-on" : ""} onClick={() => { setFilter("room"); setFilterOpen(false); }}>
                  With student and staff
                </button>
                <p>Coursework</p>
                <button type="button" className={filter === "work" ? "is-on" : ""} onClick={() => { setFilter("work"); setFilterOpen(false); }}>
                  All coursework
                </button>
                <button type="button" className={filter === "mine" ? "is-on" : ""} onClick={() => { setFilter("mine"); setFilterOpen(false); }}>
                  Mine
                </button>
                <button type="button" className={filter === "student" ? "is-on" : ""} onClick={() => { setFilter("student"); setFilterOpen(false); }}>
                  Student
                </button>
                <button type="button" className={filter === "staff" ? "is-on" : ""} onClick={() => { setFilter("staff"); setFilterOpen(false); }}>
                  Staff
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </header>
      {!canChat ? (
        <p className="al-schools-sub">View Student Chat is not granted on this membership.</p>
      ) : error ? (
        <p className="al-schools-sub" role="alert">
          {error}
        </p>
      ) : shown.length === 0 ? (
        rows.length === 0 ? (
          <div className="al-my-students-empty">
            <h2>No messages yet</h2>
            <p>{timelineEmptyCopy({ canPost, canStaff, canClasses })}</p>
          </div>
        ) : (
          <p className="al-schools-sub">Nothing in this filter yet.</p>
        )
      ) : (
        <div className="al-my-students-timeline">
          {shown.map((row) => (
            <Link key={row.key} className="al-my-students-timeline__row" to={row.href}>
              <span className={`al-my-students-badge${row.kind === "activity" ? " al-my-students-badge--work" : ""}`}>
                {row.badge}
              </span>
              <span className="al-my-students-timeline__main">
                <strong>{row.title}</strong>
                {row.detail ? <span>{row.detail}</span> : null}
                <span className="al-my-students-timeline__snippet">{row.snippet}</span>
              </span>
              <time>{row.at ? whenLabel(row.at) : ""}</time>
            </Link>
          ))}
        </div>
      )}
      {canChat ? (
        <button type="button" className="al-msg-fab" onClick={() => setStartOpen(true)}>
          <Plus size={14} strokeWidth={2.25} aria-hidden />
          New conversation
        </button>
      ) : null}
      <Dialog
        open={startOpen}
        title="New conversation"
        subtitle="Three different places. Only the first two stay on this student’s messages."
        onClose={() => setStartOpen(false)}
        width="36rem"
      >
        <div className="al-my-students-start">
          <section className="al-my-students-start__group al-my-students-start__group--student">
            <h3>About this student</h3>
            <p>Rooms for this student. They show up in the list after the first message.</p>
            <StartCard
              icon={<Users size={16} aria-hidden />}
              title="Student and staff"
              text={
                canPost
                  ? "Shared with this student and with staff who can open the room."
                  : "You can open this room, but you do not have permission to write in it."
              }
              href={roomHref}
              onOpen={() => setStartOpen(false)}
            />
            <StartCard
              icon={<Shield size={16} aria-hidden />}
              title="Staff only"
              text={canStaff ? "Staff only. The student does not see this." : "You do not have permission for this channel."}
              href={canStaff ? `${roomHref}?channel=staff` : undefined}
              unavailable={canStaff ? undefined : "No permission"}
              onOpen={() => setStartOpen(false)}
            />
            <StartCard
              icon={<NotebookPen size={16} aria-hidden />}
              title="Private notes"
              text="Only you can see these notes."
              href={`${roomHref}?channel=personal`}
              onOpen={() => setStartOpen(false)}
            />
          </section>
          <section className="al-my-students-start__group al-my-students-start__group--work">
            <h3>Coursework</h3>
            <p>A chat on one activity, not a room for the whole student.</p>
            <StartCard
              icon={<BookOpen size={16} aria-hidden />}
              title="On an activity"
              text={
                !canClasses
                  ? "You do not have permission to open this student’s classes."
                  : canPost
                    ? "Open a class and write on an activity that has no chat yet."
                    : "You can open a class, but you do not have permission to write on an activity."
              }
              href={canClasses ? `/my-students/${encodeURIComponent(studentUserId)}/courses` : undefined}
              unavailable={canClasses ? undefined : "No permission"}
              onOpen={() => setStartOpen(false)}
            />
          </section>
          <section className="al-my-students-start__group al-my-students-start__group--dm">
            <h3>Direct message</h3>
            <p>
              A private line. It does not appear in this list. If a conversation with this person already exists, a new
              thread is not created. The message is added to that conversation.
            </p>
            <StartCard
              icon={<Mail size={16} aria-hidden />}
              title="Just you and this student"
              text={
                !canDm
                  ? "You do not have permission to send a direct message."
                  : conversation?.student.studentEmail
                    ? "Opens the conversation you already have with this student. If there is none, the new-message box opens with their email filled in."
                    : "Opens the conversation you already have with this student. If there is none, the new-message box opens."
              }
              href={
                canDm
                  ? `/messages?${new URLSearchParams({
                      with: studentUserId,
                      ...(conversation?.student.studentEmail ? { email: conversation.student.studentEmail } : {}),
                    }).toString()}`
                  : undefined
              }
              unavailable={canDm ? undefined : "No permission"}
              onOpen={() => setStartOpen(false)}
            />
            {canRelationship ? (
              targetsError ? (
                <p role="alert">{targetsError}</p>
              ) : !relationshipReady ? (
                <p>Send Student Relationship has no person-link types checked.</p>
              ) : targets == null ? (
                <p>Loading linked people…</p>
              ) : targets.people.length === 0 ? (
                <p>No guardians, counselors, or other checked links on this student.</p>
              ) : (
                targets.people.map((person) => (
                  <StartCard
                    key={`${person.kind}:${person.userId}`}
                    icon={<Mail size={16} aria-hidden />}
                    title={person.name}
                    text={person.label}
                    href={directHref(studentUserId, person)}
                    onOpen={() => setStartOpen(false)}
                  />
                ))
              )
            ) : null}
            {canTeachers ? (
              targetsError ? (
                canRelationship ? null : <p role="alert">{targetsError}</p>
              ) : targets == null ? (
                <p>Loading teachers…</p>
              ) : targets.courses.length === 0 ? (
                <p>No teachers with student chat on a current class.</p>
              ) : (
                targets.courses.map((course) => (
                  <div key={course.id}>
                    <p className="al-my-students-start__course">{course.title}</p>
                    {course.teachers.map((teacher) => (
                      <StartCard
                        key={`${course.id}:${teacher.userId}`}
                        icon={<Mail size={16} aria-hidden />}
                        title={teacher.name}
                        text="Teacher"
                        href={directHref(studentUserId, teacher)}
                        onOpen={() => setStartOpen(false)}
                      />
                    ))}
                  </div>
                ))
              )
            ) : null}
          </section>
        </div>
      </Dialog>
    </div>
  );
}
function StudentCourses() {
  const { studentUserId = "" } = useParams();
  const { capabilities, loading: authLoading } = useAuth();
  const canList = Boolean(capabilities?.viewStudentsCourseEnrollments);
  const [data, setData] = useState<CourseListData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading || !canList) return;
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: CourseListData }>(
      `/api/my-students/${encodeURIComponent(studentUserId)}/courses`,
    )
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [authLoading, canList, studentUserId]);

  if (authLoading || (canList && !error && !data)) {
    return <LoadingOverlay isVisible message="Loading classes…" />;
  }

  const name = data?.student.studentName || "Student";

  return (
    <div className="al-schools">
      <header className="al-schools-header">
        <div className="al-schools-header-text">
          <Link className="al-schools-sub" to="/my-students">
            Students
          </Link>
          <h1>{name}</h1>
          <p className="al-schools-sub">Classes at this school.</p>
        </div>
      </header>
      {!canList ? (
        <p className="al-schools-sub">View Students Course Enrollments is not granted on this membership.</p>
      ) : error ? (
        <p className="al-schools-sub" role="alert">
          {error}
        </p>
      ) : data ? (
        <>
          {data.otherSchoolCount > 0 ? (
            <p className="al-schools-sub">
              This student also has {data.otherSchoolCount === 1 ? "a class" : "classes"} at another school. Switch to
              that school to see them.
            </p>
          ) : null}
          {data.courses.length === 0 ? (
            <div className="al-schools-empty">
              <p>This student is not in a class at this school yet.</p>
            </div>
          ) : (
            <div className="al-schools-list">
              {data.courses.map((course) => {
                const body = (
                  <>
                    <h2 className="al-schools-card-title">{course.title}</h2>
                    {data.canOpenContent ? <p className="al-schools-card-meta">{courseMeta(course)}</p> : null}
                  </>
                );
                return data.canOpenContent ? (
                  <Link
                    key={course.id}
                    className="al-schools-card al-schools-card--clickable al-schools-card--student"
                    to={`/my-students/${encodeURIComponent(studentUserId)}/course/${encodeURIComponent(course.id)}`}
                  >
                    <span className="al-schools-card-body">{body}</span>
                  </Link>
                ) : (
                  <article key={course.id} className="al-schools-card al-schools-card--student">
                    <div className="al-schools-card-body">{body}</div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function StudentCourse() {
  const { studentUserId = "", courseId = "" } = useParams();
  const [data, setData] = useState<CourseContent | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: CourseContent }>(
      `/api/my-students/${encodeURIComponent(studentUserId)}/courses/${encodeURIComponent(courseId)}`,
    )
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [studentUserId, courseId]);

  if (!error && !data) return <LoadingOverlay isVisible message="Loading class…" />;

  const back = `/my-students/${encodeURIComponent(studentUserId)}/courses`;
  if (error || !data) {
    return (
      <div className="al-schools">
        <Link className="al-schools-sub" to={back}>
          Classes
        </Link>
        <p className="al-schools-sub" role="alert">
          {error || "That class was not found."}
        </p>
      </div>
    );
  }

  const groups = outlineGroups(data.activities);

  return (
    <div className="student-page">
      <Link className="student-text-link" to={back}>
        <ChevronLeft size={15} /> {data.student.studentName}
      </Link>
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">Class</span>
        <h1>{data.course.title}</h1>
        <p>Published outline and this student’s work. The assignment itself stays with the student.</p>
      </div>
      <section className="student-card">
        {data.activities.length === 0 ? (
          <p className="student-row__sub">Nothing has been published in this class yet.</p>
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              {group.unitTitle || group.lessonTitle ? (
                <p className="my-students-outline-label">
                  {[group.unitTitle, group.lessonTitle].filter(Boolean).join(" · ")}
                </p>
              ) : null}
              {group.items.map((item) => (
                <div key={item.id} className="student-row">
                  <span>
                    <strong>{item.title}</strong>
                    <span className="student-row__sub">
                      {ITEM_LABEL[item.type] || "Activity"}
                      {item.percent != null && item.pointsEarned != null && item.pointsPossible != null
                        ? ` · ${item.pointsEarned} of ${item.pointsPossible} points`
                        : ""}
                    </span>
                  </span>
                  <span
                    className="student-row__sub"
                    style={item.percent != null ? { color: "var(--s-ink)", fontWeight: 800 } : undefined}
                  >
                    {scoreLabel(item)}
                  </span>
                </div>
              ))}
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function closedHint(data: ThreadData | null) {
  if (!data || data.canReply) return undefined;
  switch (data.replyBlock) {
    case "not_started":
      return `You can write here once ${data.student.studentName} opens this activity.`;
    case "chat_off":
      return "The teacher has chat turned off here. You can still read it.";
    default:
      return "You can read this chat but not write in it.";
  }
}

function StudentThread() {
  const { studentUserId = "", activityId = "" } = useParams();
  const [data, setData] = useState<ThreadData | null>(null);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [tab, setTab] = useState("all");
  const [sending, setSending] = useState(false);
  const base = `/api/my-students/${encodeURIComponent(studentUserId)}/activities/${encodeURIComponent(activityId)}/thread`;

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: ThreadData }>(base)
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [base]);

  if (error) {
    return (
      <div className="al-schools">
        <p className="al-schools-sub" role="alert">
          {error}
        </p>
      </div>
    );
  }
  if (!data) return <LoadingOverlay isVisible message="Loading chat…" />;

  return (
    <div className="student-page">
      <Link className="student-text-link" to={`/my-students/${encodeURIComponent(studentUserId)}/messages`}>
        <ChevronLeft size={16} aria-hidden /> {data.student.studentName}
      </Link>
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">
          {data.student.studentName} · {data.course.title}
        </span>
        <h1>{data.activity.title}</h1>
        <p>
          {ITEM_LABEL[data.activity.type] ? `${ITEM_LABEL[data.activity.type]} · ` : ""}
          The chat between this student and their teacher.
        </p>
      </div>
      {sendError ? (
        <div className="student-inline-error" role="alert" style={{ marginBottom: 12 }}>
          {sendError}
        </div>
      ) : null}
      <section className="student-card parent-thread-card">
        <WorkThread
          data={data}
          tab={tab}
          audience="public"
          sending={sending}
          onTabChange={setTab}
          closedHint={closedHint(data)}
          onSend={async (body) => {
            setSending(true);
            setSendError("");
            try {
              const env = await post<{ success: boolean; data: ThreadData }>(`${base}/messages`, {
                submissionId: messageSubmissionId(tab, data.attempts),
                body,
              });
              setData(env.data);
            } catch (err) {
              setSendError(errorMessage(err));
            } finally {
              setSending(false);
            }
          }}
        />
      </section>
    </div>
  );
}

function StudentRoom() {
  const { studentUserId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const channel = params.get("channel") || "public";
  const [data, setData] = useState<RoomData | null>(null);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [tab, setTab] = useState("all");
  const [sending, setSending] = useState(false);
  const back = `/my-students/${encodeURIComponent(studentUserId)}/messages`;

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: RoomData }>(
      `/api/my-students/${encodeURIComponent(studentUserId)}/room?audience=${encodeURIComponent(channel)}`,
    )
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [studentUserId, channel]);

  if (!error && !data) return <LoadingOverlay isVisible message="Loading conversation…" />;
  if (error || !data) {
    return (
      <div className="student-page">
        <Link className="student-text-link" to={back}>
          <ChevronLeft size={16} aria-hidden /> Messages
        </Link>
        <p className="al-schools-sub" role="alert">
          {error}
        </p>
      </div>
    );
  }

  const room = ROOM_META[data.audience as keyof typeof ROOM_META] || ROOM_META.public;

  return (
    <div className="student-page al-student-room">
      <Link className="al-student-room__back" to={back}>
        <ChevronLeft size={16} aria-hidden /> Messages
      </Link>
      <header className="al-student-room__head">
        <h1>{data.student.studentName}</h1>
      </header>
      {sendError ? (
        <p className="al-schools-sub" role="alert">
          {sendError}
        </p>
      ) : null}
      <section className="al-student-room__card">
        <WorkThread
          data={data}
          tab={tab}
          audience={data.audience}
          channels
          channelMeta={ROOM_META}
          emptyText={room.empty}
          placeholder={room.placeholder}
          sending={sending}
          onTabChange={setTab}
          onAudienceChange={(next) => setParams(next === "public" ? {} : { channel: next })}
          closedHint={data.canReply ? undefined : "You can read this channel."}
          onSend={async (body) => {
            setSending(true);
            setSendError("");
            try {
              const env = await post<{ success: boolean; data: RoomData }>(
                `/api/my-students/${encodeURIComponent(studentUserId)}/room/messages`,
                { audience: data.audience, body },
              );
              setData(env.data);
            } catch (err) {
              setSendError(errorMessage(err));
            } finally {
              setSending(false);
            }
          }}
        />
      </section>
    </div>
  );
}

function RedirectStudent() {
  const { studentUserId = "" } = useParams();
  return <Navigate to={`/my-students/${encodeURIComponent(studentUserId)}/courses`} replace />;
}

export default function MyStudents() {
  return (
    <Routes>
      <Route index element={<StudentHome />} />
      <Route path="conversations" element={<Navigate to="/my-students" replace />} />
      <Route path=":studentUserId/messages" element={<StudentMessages />} />
      <Route path=":studentUserId/room" element={<StudentRoom />} />
      <Route path=":studentUserId/courses" element={<StudentCourses />} />
      <Route path=":studentUserId/course/:courseId" element={<StudentCourse />} />
      <Route path=":studentUserId/conversation" element={<RedirectMessages />} />
      <Route path=":studentUserId/activity/:activityId" element={<StudentThread />} />
      <Route path=":studentUserId" element={<RedirectStudent />} />
    </Routes>
  );
}

function RedirectMessages() {
  const { studentUserId = "" } = useParams();
  return <Navigate to={`/my-students/${encodeURIComponent(studentUserId)}/messages`} replace />;
}
